import { useMemo } from 'react'
import { Separator } from '@/components/ui/separator'
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion'
import { TransactionListItem } from '@/components/transaction/transaction-list-item'
import { formatAmount, getTransactionDisplayName } from '@/lib/utils'
import { useCategoryDetail } from './context'
import { useCategoryTransactions, type CategoryTxEntry } from './use-category-transactions'

type StoreRankingItem = {
  storeName: string
  totalAmount: number
  visitCount: number
}

export function CategoryStoreRankingSection() {
  const { categoryName, filteredTransactions, currentMonth } = useCategoryDetail()
  const categoryTxs = useCategoryTransactions(filteredTransactions, categoryName)

  const { storeRanking, storeTransactions } = useMemo(() => {
    const grouped = new Map<string, CategoryTxEntry[]>()
    for (const entry of categoryTxs) {
      const storeName = getTransactionDisplayName(entry.tx)
      const arr = grouped.get(storeName) ?? []
      arr.push(entry)
      grouped.set(storeName, arr)
    }
    const sorted = Array.from(grouped.entries())
      .map(([storeName, entries]) => ({
        storeName,
        entries,
        totalAmount: entries.reduce((s, e) => s + e.categoryAmount, 0),
        visitCount: entries.length,
      }))
      .sort((a, b) => b.totalAmount - a.totalAmount)

    if (sorted.length <= 7) {
      const ranking: StoreRankingItem[] = sorted.map((s) => ({
        storeName: s.storeName,
        totalAmount: s.totalAmount,
        visitCount: s.visitCount,
      }))
      const txsByStore = new Map(sorted.map((s) => [s.storeName, s.entries] as const))
      return { storeRanking: ranking, storeTransactions: txsByStore }
    }
    const top = sorted.slice(0, 5)
    const rest = sorted.slice(5)
    const otherAmount = rest.reduce((s, r) => s + r.totalAmount, 0)
    const otherVisits = rest.reduce((s, r) => s + r.visitCount, 0)
    const otherEntries = rest
      .flatMap((r) => r.entries)
      .sort((a, b) => b.tx.date.localeCompare(a.tx.date))
    const ranking: StoreRankingItem[] = [
      ...top.map((s) => ({
        storeName: s.storeName,
        totalAmount: s.totalAmount,
        visitCount: s.visitCount,
      })),
      { storeName: 'その他', totalAmount: otherAmount, visitCount: otherVisits },
    ]
    const txsByStore = new Map<string, CategoryTxEntry[]>([
      ...top.map((s) => [s.storeName, s.entries] as const),
      ['その他', otherEntries] as const,
    ])
    return { storeRanking: ranking, storeTransactions: txsByStore }
  }, [categoryTxs])

  return (
    <>
      <Separator />
      <div className="space-y-2">
        <h3 className="text-sm font-medium text-muted-foreground">店舗別</h3>
        {categoryTxs.length === 0 ? (
          <p className="text-sm text-muted-foreground">取引データなし</p>
        ) : (
          <Accordion type="multiple">
            {storeRanking.map((store) => {
              const entries = storeTransactions.get(store.storeName) ?? []
              const isOther = store.storeName === 'その他'

              return (
                <AccordionItem
                  key={store.storeName}
                  value={`store-${store.storeName}`}
                  className="border-0"
                >
                  <AccordionTrigger className="py-1.5 px-2 hover:no-underline font-normal min-w-0 overflow-hidden">
                    <span className="flex items-center justify-between w-full text-sm pr-2 min-w-0">
                      <span
                        className={`min-w-0 truncate ${isOther ? 'text-muted-foreground' : ''}`}
                      >
                        {store.storeName}
                      </span>
                      <div className="flex items-center gap-3 shrink-0 tabular-nums">
                        <span className="text-xs text-muted-foreground">
                          {store.visitCount}回
                        </span>
                        <span>{formatAmount(store.totalAmount)}</span>
                      </div>
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="pb-2 px-2">
                    <div className="space-y-0.5 pl-2 border-l-2 border-muted ml-1">
                      {entries.map(({ tx, categoryAmount: amount }) => (
                        <TransactionListItem
                          key={tx.id}
                          transaction={tx}
                          to={`/transactions/${tx.id}?month=${currentMonth}`}
                          overrideAmount={amount}
                        />
                      ))}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              )
            })}
          </Accordion>
        )}
      </div>
    </>
  )
}
